<template>
  <div class="performance-report">
    <!-- Report Header -->
    <div class="report-header mb-4 p-4 bg-light rounded">
      <div class="row align-items-center">
        <div class="col-md-8">
          <h4 class="mb-2">Performance Review Report</h4>
          <p class="text-muted mb-0">
            Generated on {{ formatDate(new Date()) }}
          </p>
        </div>
        <div class="col-md-4 text-md-end">
          <button @click="printReport" class="btn btn-outline-primary me-2">
            <i class="icon-feather-printer me-2"></i>Print
          </button>
          <button @click="exportPDF" class="btn btn-primary">
            <i class="icon-feather-download me-2"></i>Export PDF
          </button>
        </div>
      </div>
    </div>

    <!-- Employee Information -->
    <div class="card mb-4">
      <div class="card-header bg-primary text-white">
        <h5 class="mb-0">Performance Review Information</h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-6">
            <table class="table table-sm">
              <tbody>
                <tr>
                  <td class="text-muted">Employee:</td>
                  <td><strong>{{ review.employee?.employee_no || 'N/A' }} - {{ employeeName }}</strong></td>
                </tr>
                <tr>
                  <td class="text-muted">Work Station:</td>
                  <td>{{ review.employee?.station?.name }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Department:</td>
                  <td>{{ review.employee?.department?.name }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Designation:</td>
                  <td>{{ review.employee?.designation?.name }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="col-md-6">
            <table class="table table-sm">
              <tbody>
                <tr>
                  <td class="text-muted">Review Cycle:</td>
                  <td>{{ review.review_cycle?.name || 'N/A' }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Period:</td>
                  <td>{{ formatDate(review.review_cycle?.start_date) }} - {{ formatDate(review.review_cycle?.end_date) }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Reviewer:</td>
                  <td>{{ reviewerName }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Status:</td>
                  <td>
                    <span :class="getStatusBadgeClass(review.status)">
                      {{ formatStatus(review.status) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Overall Performance Score -->
    <div class="card mb-4">
      <div class="card-header bg-primary text-white">
        <h5 class="mb-0">Overall Performance</h5>
      </div>
      <div class="card-body">
        <div class="row align-items-center">
          <div class="col-md-4 text-center">
            <div class="circular-progress mb-3">
              <svg width="150" height="150">
                <circle
                  cx="75"
                  cy="75"
                  r="65"
                  stroke="#e9ecef"
                  stroke-width="10"
                  fill="none"
                />
                <circle
                  cx="75"
                  cy="75"
                  r="65"
                  :stroke="getScoreColor(overallScore)"
                  stroke-width="10"
                  fill="none"
                  :stroke-dasharray="`${(overallScore / 100) * 408} 408`"
                  stroke-dashoffset="102"
                  transform="rotate(-90 75 75)"
                />
              </svg>
              <div class="score-text">
                <h2 class="mb-0">{{ overallScore }}%</h2>
                <small class="text-muted">Overall Score</small>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <h4 class="mb-3">Performance Rating</h4>
            <div class="text-center">
              <span :class="getRatingBadgeClass(review.rating)" style="font-size: 1.2rem; padding: 0.5rem 1rem;">
                {{ formatRating(review.rating) }}
              </span>
            </div>
          </div>
          <div class="col-md-4" v-if="scoresByCategory && Object.keys(scoresByCategory).length">
            <h4 class="mb-3">Score Breakdown</h4>
            <div class="score-breakdown">
              <div v-for="(section, sectionName) in scoresByCategory" :key="sectionName" class="mb-2">
                <span class="text-muted">{{ sectionName }}:</span>
                <strong class="float-end">{{ section.average_score ?? 'N/A' }}%</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Scores By Section/Template -->
    <div class="card mb-4">
      <div class="card-header bg-primary text-white">
        <h5 class="mb-0">Scores</h5>
      </div>
      <div class="card-body">
        <div v-if="scoresByCategory && Object.keys(scoresByCategory).length">
          <div v-for="(section, sectionName) in scoresByCategory" :key="sectionName" class="mb-4">
            <div class="d-flex justify-content-between mb-1">
              <span class="fw-semibold">{{ sectionName }}</span>
              <span class="text-muted">Avg: {{ section.average_score ?? 'N/A' }}%</span>
            </div>
            <div v-if="section.responses && section.responses.length" class="table-responsive">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Question</th>
                    <th style="width: 120px;" class="text-end">Score</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="resp in section.responses" :key="resp.id">
                    <td>{{ resp.question_text }}</td>
                    <td class="text-end">{{ resp.score }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div v-else class="text-center text-muted py-3">
          No scores available
        </div>
      </div>
    </div>

    <!-- Strengths and Areas for Improvement -->
    <div class="row mb-4">
      <div class="col-md-6">
        <div class="card h-100">
          <div class="card-header bg-success text-white">
            <h5 class="mb-0">Key Strengths</h5>
          </div>
          <div class="card-body">
            <p v-if="review.strengths">{{ review.strengths }}</p>
            <p v-else class="text-muted">No strengths documented</p>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="card h-100">
          <div class="card-header bg-warning">
            <h5 class="mb-0">Areas for Improvement</h5>
          </div>
          <div class="card-body">
            <p v-if="review.areas_for_improvement">{{ review.areas_for_improvement }}</p>
            <p v-else class="text-muted">No areas for improvement documented</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Manager's Summary -->
    <div class="card mb-4">
      <div class="card-header bg-primary text-white">
        <h5 class="mb-0">Manager's Summary</h5>
      </div>
      <div class="card-body">
        <p v-if="review.summary">{{ review.summary }}</p>
        <div v-if="review.manager_comments" class="mt-3">
          <h6 class="text-muted">Manager Comments</h6>
          <p class="mb-0">{{ review.manager_comments }}</p>
        </div>
        <p v-else class="text-muted">No summary provided</p>
      </div>
    </div>

    <!-- 360 Feedback Summary -->
    <div class="card mb-4" v-if="feedbackSummary && Object.keys(feedbackSummary).length">
      <div class="card-header bg-info text-white">
        <h5 class="mb-0">360 Feedback Summary</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6" v-for="(group, relation) in feedbackSummary" :key="relation">
            <div class="p-3 rounded border h-100">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0 text-capitalize">{{ relation }}</h6>
                <span class="badge bg-secondary">{{ group.count }} response(s)</span>
              </div>
              <div v-if="group.feedbacks && group.feedbacks.length" class="small text-muted">
                Latest overall rating: <strong>{{ group.feedbacks[0]?.overall_rating || 'N/A' }}</strong>
                <span class="ms-2">Avg score: <strong>{{ group.feedbacks[0]?.average_score ?? 'N/A' }}</strong></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Year Over Year -->
    <div class="card" v-if="yearOverYear && yearOverYear.length">
      <div class="card-header bg-dark text-white">
        <h5 class="mb-0">Year-over-Year</h5>
      </div>
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Cycle</th>
                <th>Overall Score</th>
                <th>Rating</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in yearOverYear" :key="row.cycle">
                <td>{{ row.cycle }}</td>
                <td>{{ row.overall_score ?? 'N/A' }}</td>
                <td>{{ row.rating ? formatRating(row.rating) : 'N/A' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import moment from 'moment';
import notify from '@/utils/notify';

export default {
  name: 'PerformanceReport',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const review = computed(() => props.data.review || {});
    const employeeName = computed(() => {
      const e = review.value.employee || {};
      return [e.first_name, e.middle_name, e.last_name].filter(Boolean).join(' ') || 'N/A';
    });
    const reviewerName = computed(() => {
      const r = review.value.reviewer || {};
      return r.full_name || [r.first_name, r.last_name].filter(Boolean).join(' ') || 'N/A';
    });
    const overallScore = computed(() => {
      const s = review.value.overall_score;
      const num = typeof s === 'string' ? parseFloat(s) : s;
      return isNaN(num) ? 0 : Math.round(num);
    });
    const scoresByCategory = computed(() => props.data.scores_by_category || {});
    const feedbackSummary = computed(() => props.data.feedback_summary || {});
    const yearOverYear = computed(() => props.data.year_over_year || []);
    const formatDate = (date) => {
      if (!date) return 'N/A';
      return moment(date).format('MMMM DD, YYYY');
    };

    const formatStatus = (status) => {
      const statusMap = {
        pending: 'Pending',
        self_review: 'Self Review',
        manager_review: 'Manager Review',
        calibration: 'Calibration',
        completed: 'Completed'
      };
      return statusMap[status] || status;
    };

    const formatRating = (rating) => {
      const ratingMap = {
        exceptional: 'Exceptional',
        exceeds: 'Exceeds Expectations',
        meets: 'Meets Expectations',
        needs_improvement: 'Needs Improvement',
        unsatisfactory: 'Unsatisfactory'
      };
      return ratingMap[rating] || rating;
    };

    const formatGoalStatus = (status) => {
      const statusMap = {
        not_started: 'Not Started',
        in_progress: 'In Progress',
        at_risk: 'At Risk',
        completed: 'Completed',
        cancelled: 'Cancelled'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classes = {
        pending: 'badge bg-secondary',
        self_review: 'badge bg-warning',
        manager_review: 'badge bg-info',
        calibration: 'badge bg-primary',
        completed: 'badge bg-success'
      };
      return classes[status] || 'badge bg-secondary';
    };

    const getRatingBadgeClass = (rating) => {
      const classes = {
        exceptional: 'badge bg-success',
        exceeds: 'badge bg-primary',
        meets: 'badge bg-info',
        needs_improvement: 'badge bg-warning',
        unsatisfactory: 'badge bg-danger'
      };
      return classes[rating] || 'badge bg-secondary';
    };

    const getGoalStatusBadgeClass = (status) => {
      const classes = {
        not_started: 'badge bg-secondary',
        in_progress: 'badge bg-primary',
        at_risk: 'badge bg-danger',
        completed: 'badge bg-success',
        cancelled: 'badge bg-dark'
      };
      return classes[status] || 'badge bg-secondary';
    };

    const getScoreBarClass = (score) => {
      if (score >= 90) return 'bg-success';
      if (score >= 75) return 'bg-primary';
      if (score >= 60) return 'bg-info';
      if (score >= 40) return 'bg-warning';
      return 'bg-danger';
    };

    const getProgressBarClass = (progress) => {
      if (progress >= 75) return 'bg-success';
      if (progress >= 50) return 'bg-info';
      if (progress >= 25) return 'bg-warning';
      return 'bg-danger';
    };

    const getScoreColor = (score) => {
      if (score >= 90) return '#28a745';
      if (score >= 75) return '#007bff';
      if (score >= 60) return '#17a2b8';
      if (score >= 40) return '#ffc107';
      return '#dc3545';
    };

    const printReport = () => {
      window.print();
    };

    const exportPDF = () => {
      // This would typically call an API endpoint to generate PDF
      notify.info('PDF export functionality will be implemented with backend integration');
    };

    return {
      // data
      review,
      employeeName,
      reviewerName,
      overallScore,
      scoresByCategory,
      feedbackSummary,
      yearOverYear,
      // utils
      formatDate,
      formatStatus,
      formatRating,
      formatGoalStatus,
      getStatusBadgeClass,
      getRatingBadgeClass,
      getGoalStatusBadgeClass,
      getScoreBarClass,
      getProgressBarClass,
      getScoreColor,
      printReport,
      exportPDF
    };
  }
};
</script>

<style scoped>
.circular-progress {
  position: relative;
  display: inline-block;
}

.score-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.signature-box {
  min-height: 150px;
}

@media print {
  .btn {
    display: none;
  }

  .card {
    break-inside: avoid;
  }
}
</style>
